import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Group, Loader, Modal, SimpleGrid, Stack, Text } from '@mantine/core';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { DatasetPreview, StaticRevision } from '@/types/datasets.ts';
import { get_dataset_url, get_static_revisions_url } from '@/utils/urls.ts';

export type StaticRevisionReviewSelection = {
  versionId: string;
  before: Record<string, string>;
  selections: Record<string, string>;
  names: Record<string, string>;
};

type Impact = {
  surface: string;
  status: string;
  reason: string;
  affected_step_ids: string[];
};
type PreviewResponse = {
  base_version_id: string;
  latest_version_id: string;
  stale_base_version: boolean;
  apply_eligibility: { eligible: boolean; blocking_reasons: string[] };
  impact_report: { evidence_needs: Impact[] } | null;
  normalized_config: { static_bindings: Record<string, string> } | null;
};
type Snapshot = { revision: StaticRevision; preview: DatasetPreview };

const sameSelections = (left: Record<string, string>, right: Record<string, string>) =>
  JSON.stringify(Object.entries(left).sort()) === JSON.stringify(Object.entries(right).sort());

export default function StaticRevisionReview({
  automationId,
  selection,
  pending,
  disabled,
  onClose,
  onConfirm,
}: {
  automationId: string;
  selection: StaticRevisionReviewSelection;
  pending: boolean;
  disabled: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const api = useApiClient();
  const review = useQuery({
    queryKey: ['static-adoption-review', automationId, selection],
    retry: false,
    // Evidence can change while a modal is closed, even for immutable data.
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const snapshot = async (sourceId: string, revisionId?: string): Promise<Snapshot | null> => {
        if (!revisionId) return null;
        const { data: revision } = await api.get<StaticRevision>(
          `${get_static_revisions_url(sourceId)}/${revisionId}`,
        );
        if (revision.status !== 'ready' || !revision.snapshot_dataset_id)
          throw new Error(
            'A selected revision is no longer ready. Close this review and refresh the data.',
          );
        const { data: preview } = await api.get<DatasetPreview>(
          `${get_dataset_url(revision.snapshot_dataset_id)}/preview`,
          { params: { max_rows: 5, max_cell_chars: 200 } },
        );
        if (!preview?.dataset || preview.error != null || preview.preview_kind === 'unavailable')
          throw new Error(
            `Revision ${revisionId} could not be reviewed: ${preview?.error || 'Snapshot preview unavailable.'}`,
          );
        return { revision, preview };
      };
      const [compiled, changes] = await Promise.all([
        api.post<PreviewResponse>(
          `/automations/${automationId}/builder-authoring/compile-preview`,
          {
            base_version_id: selection.versionId,
            expected_latest_version_id: selection.versionId,
            operations: [],
            static_bindings: selection.selections,
          },
        ),
        Promise.all(
          Object.entries(selection.selections)
            .filter(([id, revision]) => selection.before[id] !== revision)
            .map(async ([id, revision]) => {
              const [before, after] = await Promise.all([
                snapshot(id, selection.before[id]),
                snapshot(id, revision),
              ]);
              return { id, before, after };
            }),
        ),
      ]);
      const preview = compiled.data;
      if (
        preview.base_version_id !== selection.versionId ||
        preview.latest_version_id !== selection.versionId ||
        preview.stale_base_version
      )
        throw new Error('The draft changed. Close this review and review the latest draft.');
      if (
        !preview.normalized_config ||
        !sameSelections(preview.normalized_config.static_bindings ?? {}, selection.selections) ||
        !preview.impact_report
      )
        throw new Error(
          'The server could not verify these selections and their evidence requirements.',
        );
      return { preview, changes };
    },
  });
  const eligible =
    review.data?.preview.apply_eligibility.eligible && review.data.changes.length > 0;
  return (
    <Modal
      opened
      onClose={onClose}
      title="Review data revision changes"
      size="xl"
      closeOnClickOutside={!pending}
      closeOnEscape={!pending}
      withCloseButton={!pending}
    >
      <Stack>
        <Text size="sm">
          Compare the recorded data below before creating a new draft. This does not publish or run
          the automation.
        </Text>
        {review.isPending && (
          <Group>
            <Loader size="sm" />
            <Text>Loading data and evidence review…</Text>
          </Group>
        )}
        {review.isError && (
          <Alert color="red" title="Review unavailable" role="alert">
            {review.error.message}
            <StyledButton variant="outline" mt="sm" onClick={() => void review.refetch()}>
              Retry review
            </StyledButton>
          </Alert>
        )}
        {review.data?.changes.map((change) => (
          <Stack key={change.id} gap="xs">
            <Text fw={600} style={{ overflowWrap: 'anywhere' }}>
              {selection.names[change.id] ?? change.id}
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <SnapshotPreview label="Current draft data" snapshot={change.before} />
              <SnapshotPreview label="Selected data" snapshot={change.after} />
            </SimpleGrid>
          </Stack>
        ))}
        {review.data && (
          <Stack gap="xs">
            <Text fw={600}>Affected evidence</Text>
            <Text size="sm">
              Existing runs remain attached to their original version. They do not prove this new
              data selection; review and test the new draft before publishing.
            </Text>
            {review.data.preview.impact_report?.evidence_needs.map((need, index) => (
              <Alert key={`${need.surface}-${index}`} color="yellow">
                <Text size="sm">{need.reason}</Text>
                {need.affected_step_ids.length > 0 && (
                  <Text size="xs" mt={4} style={{ overflowWrap: 'anywhere' }}>
                    Steps to review: {need.affected_step_ids.join(', ')}
                  </Text>
                )}
              </Alert>
            ))}
            {review.data.preview.apply_eligibility.blocking_reasons.map((reason) => (
              <Alert key={reason} color="red">
                {reason}
              </Alert>
            ))}
          </Stack>
        )}
        {disabled && <Text size="sm">Wait for pending edits, then review the latest draft.</Text>}
        <Group justify="flex-end">
          <StyledButton variant="outline" color="gray" onClick={onClose} disabled={pending}>
            Cancel
          </StyledButton>
          <StyledButton
            variant="primary"
            onClick={onConfirm}
            loading={pending}
            disabled={!eligible || disabled || review.isFetching || review.isError}
          >
            Create draft with selected revisions
          </StyledButton>
        </Group>
      </Stack>
    </Modal>
  );
}

function SnapshotPreview({ label, snapshot }: { label: string; snapshot: Snapshot | null }) {
  const preview = snapshot?.preview;
  return (
    <Box p="sm" style={{ border: '1px solid var(--mantine-color-default-border)', minWidth: 0 }}>
      <Stack gap={4}>
        <Text size="sm" fw={600}>
          {label}
        </Text>
        {!snapshot ? (
          <Text size="sm">No recorded revision. Historical data cannot be reconstructed.</Text>
        ) : (
          <>
            <Text size="xs" style={{ overflowWrap: 'anywhere' }}>
              Revision {snapshot.revision.id}
            </Text>
            <Text size="xs">
              {preview?.dataset.name} · {new Date(snapshot.revision.created_at).toLocaleString()}
            </Text>
            {preview?.error ? (
              <Text size="sm">{preview.error}</Text>
            ) : (
              <Box
                component="pre"
                m={0}
                p="xs"
                style={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  maxHeight: 240,
                  overflow: 'auto',
                  fontSize: 12,
                }}
              >
                {JSON.stringify(
                  preview?.rows != null
                    ? { columns: preview.columns, rows: preview.rows }
                    : preview?.child_datasets != null
                      ? preview.child_datasets.map((child) => ({
                          name: child.name,
                          type: child.dataset_type,
                        }))
                      : preview?.value,
                  null,
                  2,
                ) ?? 'No preview content available.'}
              </Box>
            )}
            <Text size="xs" c="dimmed">
              Bounded preview · {preview?.total_count ?? 0} total items
              {preview?.truncated ? ' · additional content not shown' : ''}
            </Text>
          </>
        )}
      </Stack>
    </Box>
  );
}
